import React, { useMemo } from 'react';
import { getInventoryStyleDisplayGroups, getStoreStyleName } from '../utils/augments';
import { formatAchievementLeagueLabel, isCoinAchievementLeague } from '../utils/formatAchievementLeague';
import '../pages/Profile.css';

function formatAwardedAt(epochSeconds) {
  if (epochSeconds == null || Number.isNaN(Number(epochSeconds))) return null;
  const d = new Date(Number(epochSeconds) * 1000);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function ordinalPlace(rank) {
  const n = Number(rank);
  if (!Number.isFinite(n) || n < 1) return `${rank} place`;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th place`;
  switch (n % 10) {
    case 1:
      return `${n}st place`;
    case 2:
      return `${n}nd place`;
    case 3:
      return `${n}rd place`;
    default:
      return `${n}th place`;
  }
}

/**
 * Group achievements by rank (ascending), newest first within each rank.
 * @param {Array<{ leagueCode?: string, rank?: number, awardedAt?: number }>} achievements
 */
function groupAchievementsByRank(achievements) {
  const byRank = new Map();
  for (const a of achievements ?? []) {
    const rank = typeof a.rank === 'number' ? a.rank : Number(a.rank) || 0;
    if (!byRank.has(rank)) byRank.set(rank, []);
    byRank.get(rank).push(a);
  }
  return [...byRank.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rank, items]) => ({
      rank,
      items: [...items].sort((a, b) => (Number(b.awardedAt) || 0) - (Number(a.awardedAt) || 0))
    }));
}

/**
 * Read-only inventory (augment styles) + achievements (shared by public Profile and Account).
 * @param {{ s?: Record<string, number[]> }} inventory
 */
const ProfileInventoryAchievements = ({ inventory = {}, achievements = [] }) => {
  const styleGroups = useMemo(() => getInventoryStyleDisplayGroups(inventory), [inventory]);
  const rankGroups = useMemo(() => groupAchievementsByRank(achievements), [achievements]);

  return (
    <>
      <div className="sc-card profile-section">
        <h2 className="profile-section-title">Achievements</h2>
        {rankGroups.length === 0 ? (
          <p className="profile-empty">No top-10 league finishes recorded yet.</p>
        ) : (
          <ul className="profile-ach-lines">
            {rankGroups.map(({ rank, items }) => (
              <li key={rank} className="profile-ach-line">
                <strong className="profile-ach-rank">{ordinalPlace(rank)}:</strong>{' '}
                {items.map((a, idx) => {
                  const date = formatAwardedAt(a.awardedAt);
                  const title = date ? `Awarded on ${date}` : undefined;
                  return (
                    <React.Fragment key={`${a.leagueCode}-${a.awardedAt}-${idx}`}>
                      {idx > 0 ? <span className="profile-ach-sep">, </span> : null}
                      <span className="profile-ach-entry" title={title}>
                        <span className="profile-ach-league-label">
                          {formatAchievementLeagueLabel(a.leagueCode)}
                        </span>
                        {isCoinAchievementLeague(a.leagueCode) ? (
                          <img src="/coin.svg" alt="Coin league" className="profile-ach-coin-icon" />
                        ) : null}
                      </span>
                    </React.Fragment>
                  );
                })}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="sc-card profile-section">
        <h2 className="profile-section-title">Play styles</h2>
        <p className="profile-section-hint">Owned alternate art for match, clash, and round plays</p>
        {styleGroups.length === 0 ? (
          <p className="profile-empty">No play styles yet. Visit the store to buy alternate art.</p>
        ) : (
          <div className="profile-inventory-groups">
            {styleGroups.map(({ group, rows }) => (
              <section key={group.id} className={`profile-inventory-group profile-inventory-group--${group.color}`}>
                <h3 className="profile-inventory-group-name">{group.label}</h3>
                <ul className="profile-inventory-list profile-inventory-list--styles">
                  {rows.map(({ item, styleId, styleName }) => {
                    const styleSvgs = item.styleSvgs?.length
                      ? item.styleSvgs
                      : [item.styleSvg].filter(Boolean);
                    return (
                      <li key={`${item.id}-${styleId}`} className="profile-inventory-item profile-inventory-item--style">
                        <span
                          className={`profile-inventory-style-art${
                            styleSvgs.length > 1 ? ' profile-inventory-style-art--pair' : ''
                          }`}
                        >
                          {styleSvgs.map((src) => (
                            <img key={src} src={src} alt="" className="profile-inventory-style-img" />
                          ))}
                        </span>
                        <span className="profile-inventory-style-name">{styleName ?? getStoreStyleName(styleId)}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ProfileInventoryAchievements;
